import {ModulesType} from '../../enum';
import {Status} from '../reusables';
import {ClubCurrencyType} from '../../enum/club-currency';
import {Module} from './module';
import {Webhook} from './webhook';
import {Website} from './website';
import {ClubStatusTranslationsService} from 'src/app/shared/translations';

/**
 * Blueprint for the club-overview entity.
 */
export class Club {
  public '@id': string;
  public id: number;
  public name: string;
  public street: string;
  public postCode: string;
  public city: string;
  public country: string;
  public telephone: string;
  public email: string;
  public bankName: string;
  public creditorId: number;
  public IBAN: string;
  public BIC: string;
  public checked: any;
  public logo: any;
  public status: string;
  public primaryColor: string;
  public pathToLogo?: string;
  public tixxMembershipGroupId?: string;
  public tixxExternalSso: string;
  public tixxCustomerMergeAllowed: string;
  public gdprSettingsOrdersRetentionPeriodInYears: number;
  public preferredCurrency: ClubCurrencyType;

  public modules: Array<Module> = null;
  public webhooks: Array<Webhook> = null;
  public website: Website = null;

  public statuses: Array<Status> = [
    { value: 'active', status: this.statusTranslations.i18nActiveStatus, class: 'esf-tag--success' },
    { value: 'inactive', status: this.statusTranslations.i18nInactiveStatus, class: 'esf-tag--alert' }
  ];

  public constructor(club: any, private statusTranslations: ClubStatusTranslationsService) {
    this['@id'] = club['@id'];
    this.id = club.id;
    this.name = club.name;
    this.street = club.street;
    this.postCode = club.postCode;
    this.city = club.city;
    this.country = club.country;
    this.telephone = club.telephone;
    this.email = club.email;
    this.bankName = club.bankName;
    this.creditorId = club.creditorId;
    this.IBAN = club.IBAN;
    this.BIC = club.BIC;
    this.logo = club.logo;
    this.status = club.status;
    this.primaryColor = club.primaryColor;
    this.pathToLogo = club.logo?.contentUrl;
    this.tixxMembershipGroupId = club.tixxMembershipGroupId;
    this.tixxExternalSso = club.tixxExternalSso;
    this.tixxCustomerMergeAllowed = club.tixxCustomerMergeAllowed;
    this.gdprSettingsOrdersRetentionPeriodInYears = club.gdprSettingsOrdersRetentionPeriodInYears;
    this.preferredCurrency = club.preferredCurrency;
  }

  public get isCrmMembershipActive(): boolean {
    if (this.modules) {
      const crmMembership = this.modules.find(module => module.name === ModulesType.CRM_MEMBERSHIP);
      return crmMembership.status === 'active';
    }
    return false;
  }

  public get isCampaiMembershipActive(): boolean {
    if (this.modules) {
      const campaiMembership = this.modules.find(module => module.name === ModulesType.CAMPAI_MEMBERSHIP);
      return campaiMembership.status === 'active';
    }
    return false;
  }

  public get isCampaignActive(): boolean {
    if (this.modules) {
      const campaign = this.modules.find(module => module.name === ModulesType.CAMPAIGN);
      const lottery = this.modules.find(module => module.name === ModulesType.LOTTERY);
      return campaign.status === 'active' || lottery.status === 'active';
    }
    return false;
  }

  public get isMerchandisingActive(): boolean {
    if (this.modules) {
      const merchandising = this.modules.find(module => module.name === ModulesType.MERCHANDISING);
      return merchandising.status === 'active';
    }
    return false;
  }

  public get isLotteryActive(): boolean {
    if (this.modules) {
      const lottery = this.modules.find(module => module.name === ModulesType.LOTTERY);
      return lottery.status === 'active';
    }
    return false;
  }

  public get isDataFeedActive(): boolean {
    if (this.modules) {
      const dataFeed = this.modules.find(module => module.name === ModulesType.EVENTIM_DATA_FEED);
      return dataFeed.status === 'active';
    }
    return false;
  }

  public get isCustomerScoreActive(): boolean {
    if (this.modules) {
      const customerScore = this.modules.find(module => module.name === ModulesType.CUSTOMER_SCORE);
      return customerScore.status === 'active';
    }
    return false;
  }

  public get translatedClubStatus(): string {
    const translatedStatus = this.statuses.find((status: Status) => this.status === status.value);

    return translatedStatus.status;
  }

  public get statusCssClass(): any {
    const newStatus = this.statuses.find((status: Status) => this.status === status.value);
    return newStatus.class;
  }

  public get defaultWebhook(): any {
    const webhook = this.webhookExists('customer');
    return webhook ? 'invoice' : 'customer';
  }

  /**
   * Check if this webhook exists
   */
  public webhookExists(webhook: string): any {
    return this.webhooks.some((el: any) => el.name === webhook);
  }

}
